// @flow

import React, { Component } from 'react';
import { Link, NavLink, matchPath, generatePath, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import moment from 'moment';
import authService from '../services/auth-service';
import { logout } from '../actions/auth';
import { supportedLanguages } from '../copy/localization';
import type Locale from '../types/locale';
import type { UserIncludeOnboarding } from '../types/user';
import NavDropdown from './shared/NavDropdown';
import Text from './shared/Typography';
import apiService from '../services/api-service';
import { getRouteTFunction } from '../i18n';

type Props = {
  history: History,
  logout: () => void,
  location: {
    pathname: string,
    state?: {
      user: ?UserIncludeOnboarding
    }
  },
  logoutUser: any,
  user: {
    loading: boolean,
    data: UserIncludeOnboarding
  },
  className: string,
  partnerLogo?: any
} & WithTranslation

type State = {
  open: boolean,
  locale: Locale
}

type AuthNavProps = {
  open: boolean,
  isOpen: boolean,
  toggle: any,
  logoutUser: any,
  availableLanguages: any[],
  onChangeLanguage: (lang: Locale) => void,
  locale: Locale,
  user: {
    loading: boolean,
    data: UserIncludeOnboarding
  },
  t: any
}

type PublicNavProps = {
  isOpen: boolean,
  availableLanguages: any[],
  onChangeLanguage: (lang: Locale) => void,
  t: any
}

const ProfileLinks = (onLogout, t, routesTFunction) => [
  { title: t('profile_links.edit_profile'), url: routesTFunction('redirectPaths./profile'), internal: true },
  {
    title: t('profile_links.integrations'),
    url: routesTFunction('redirectPaths./profile/integrations'),
    internal: true
  },
  { title: t('profile_links.log_out'), url: '#', onClick: onLogout, internal: true }
];

// const LanguageLinks = (onChangeLanguage, availableLanguages) => availableLanguages.map((language) => (
//   { title: language.name, url: '#', onClick: () => onChangeLanguage(language), internal: true }
// ));

const LogoutLinks = (onClick, t) => [
  { title: t('profile_links.log_out'), url: '#', onClick: onClick, internal: true }
];

const HelpLinks = (t) => [
  { title: t('help_links.help_centre'), url: 'http://help.dothealth.ca/', internal: false },
  { title: t('help_links.privacy_policy'), url: 'https://www.dothealth.ca/privacy-policy/', internal: false },
  { title: t('help_links.terms_of_service'), url: 'https://www.dothealth.ca/terms-of-service/', internal: false },
  { title: t('help_links.legal'), url: 'https://www.dothealth.ca/patientaccess/', internal: false }
];

const AuthNav = ({ logoutUser, user, isOpen, t }: AuthNavProps) => {
  const allowedStates = [
    'AwaitingVerification',
    'AwaitingIDVerification',
    'AwaitingEmailVerification',
    'IDRejected',
    'Verified',
    'LegacyUnverified',
    'Completed',
    'ProfileCreated'
  ];

  const canAccessDashboard = !user.loading
    && user.data
    && allowedStates.indexOf(user.data.onboarding_state && user.data.onboarding_state.state) > -1;

  const verified = !user.loading
    && user.data
    && user.data.onboarding_state
    && user.data.onboarding_state.state === 'Verified';

  const completed = !user.loading
    && user.data
    && user.data.onboarding_state
    && user.data.onboarding_state.state === 'Completed';
  const routesTFunction = getRouteTFunction();

  return (
    <div className={`navbar-menu ${isOpen ? 'is-active' : ''}`}>
      <ul className='navbar-left'>
        {canAccessDashboard ?
          <li>
            <NavLink
              className='navbar-link'
              activeClassName='is-active'
              to={routesTFunction('redirectPaths./dashboard')}
            >{t('my_records')}</NavLink>
          </li>
          : ''}
        {completed || verified ?
          <li>
            <NavLink
              className='navbar-link'
              to={routesTFunction('redirectPaths./providers')}
              activeClassName='is-active'
            >{t('my_health_providers')}</NavLink>
          </li>
          : ''}
      </ul>
      <ul className='navbar-right'>
        {/* <li className='navbar-link__sub-menu'>
          <Text link className='navbar-link'>
            {t('languageToggle.languageHeading')}
          </Text>
          <NavDropdown
            links={LanguageLinks(onChangeLanguage, availableLanguages)}
            className='navbar-link__sub-menu--language'
          />
        </li> */}
        <li className='navbar-link__sub-menu'>
          <Text link href='http://help.dothealth.ca/' className='navbar-link'>
            {t('help')}
          </Text>
          <NavDropdown links={HelpLinks(t)} className='navbar-link__sub-menu--help' />
        </li>
        {canAccessDashboard ?
          <li className='navbar-link__sub-menu'>
            <NavLink
              className='navbar-link'
              activeClassName='is-active'
              to={routesTFunction('redirectPaths./profile')}
            >
              {t('profile')}
            </NavLink>
            <NavDropdown
              links={ProfileLinks(logoutUser, t, routesTFunction)}
              className='navbar-link__sub-menu--profile'
            />
          </li>
          : <li className='navbar-link__sub-menu'>
            <NavLink
              className='navbar-link'
              activeClassName='is-active'
              to={routesTFunction('redirectPaths./profile')}
            >
              {t('profile')}
            </NavLink>
            <NavDropdown links={LogoutLinks(logoutUser, t)} className='navbar-link__sub-menu--profile' />
          </li>}
      </ul>
    </div>
  );
};

const PublicNav = ({ isOpen, t }: PublicNavProps) => {
  const routesTFunction = getRouteTFunction();
  return (
    <div className={`navbar-menu ${isOpen ? 'is-active' : ''}`}>
      <ul className='navbar-right'>
        {/* <li className='navbar-link__sub-menu'>
          <Text link className='navbar-link'>
            {t('languageToggle.languageHeading')}
          </Text>
          <NavDropdown
            links={LanguageLinks(onChangeLanguage, availableLanguages)}
            className='navbar-link__sub-menu--language'
          />
        </li> */}
        <li className='navbar-link__sub-menu'>
          <Text link href='http://help.dothealth.ca/' className='navbar-link'>
            {t('help')}
          </Text>
          <NavDropdown links={HelpLinks(t)} className='navbar-link__sub-menu--help' />
        </li>
        <li><Link to={routesTFunction('redirectPaths./login')} className='navbar-link'>{t('log_in')}</Link></li>
        <li><Link to={routesTFunction('redirectPaths./signup')} className='navbar-link'>{t('sign_up')}</Link></li>
      </ul>
    </div>
  );
};

class AppNav extends Component<Props, State> {
  props: Props

  state: State = {
    open: false,
    locale: supportedLanguages.find((lang) =>
      lang.code === this.props.i18n.language) ||
      supportedLanguages[0] // default to English
  }

  componentDidMount = () => {
    moment.locale(this.props.i18n.language);
  }

  toggleMenu = () => {
    this.setState({
      open: !this.state.open
    });
  }

  onChangeLanguage = (lang: Locale) => {
    const i18n = this.props.i18n;
    const previousLang = i18n.language;
    const routesResourceBundle = i18n.getResourceBundle(previousLang, 'routes');

    const routerPaths = routesResourceBundle.routerPaths;

    const match = this.props.match;
    const { url, isExact } = match;

    let matchPathResponse = null;
    let routeKey = '';

    Object.keys(routerPaths).forEach((key) => {
      const translatedRoute = routerPaths[key];

      const matchPathCheck = matchPath(url, { exact: isExact, path: translatedRoute });

      if (matchPathCheck) {
        matchPathResponse = matchPathCheck;
        routeKey = key;
      }
    });

    if (matchPathResponse) {
      i18n.changeLanguage(lang.code, (err, t) => {
        if (err) {
          /**
           * Although this should be a rare scenario, if there is an error this would do a full page refresh
           * but redirect the user back to the home page
           * While testing to get an error, it turns out that i18n sets the locale in the localstorage properly
           * before the actual error is thrown which on full page refresh should set the locale to user's locale
           */
          return;
        }
        // always check that new locales are supported by moment or else define them ourselves
        moment.locale(lang.code.toLowerCase()); // moment uses lowercase codes
        apiService.setLocale(lang.code);
        const newPath = generatePath(t(`routes|routerPaths.${routeKey}`, { nsSeparator: '|' }), {
          ...matchPathResponse.params
        });

        this.setState({ locale: lang });
        window.location.href = newPath;
      });
    } else {
      i18n.changeLanguage(lang.code, () => {
        window.location.href = '/';
      });
    }
  };

  render() {
    const { i18n, t, partnerLogo } = this.props;

    const nav = authService.loggedIn ? (
      <AuthNav
        className={`${this.state.open ? 'is-active' : ''} navbar-menu`}
        open={this.state.open}
        toggle={this.toggleMenu}
        logoutUser={this.props.logoutUser}
        onChangeLanguage={(lang) => this.onChangeLanguage(lang, i18n)}
        availableLanguages={supportedLanguages}
        locale={this.state.locale}
        user={this.props.user}
        isOpen={this.state.open}
        t={t}
      />
    ) : (
      <PublicNav
        isOpen={this.state.open}
        onChangeLanguage={(lang) => this.onChangeLanguage(lang, i18n)}
        locale={this.state.locale}
        availableLanguages={supportedLanguages}
        t={t}
      />
    );
    return (
      <nav className={`navbar ${this.props.className}`}>
        <div className='container'>
          <div className={'navbar-brand'}>
            {partnerLogo || <Link className='navbar-item logo' to='/'>{t('dot_health')}</Link>}
            <div className='navbar-burger'>
              <a
                role='button'
                className={`navbar-burger ${this.state.open ? 'is-active' : ''}`}
                aria-label='menu'
                aria-expanded='false'
                onClick={this.toggleMenu}
                tabIndex={0}
              >
                <span aria-hidden='true' />
                <span aria-hidden='true' />
                <span aria-hidden='true' />
              </a>
            </div>
          </div>
          {this.props.user ? nav : ''}
        </div>
      </nav>
    );
  }
}

const mapStateToProps = (appState) => ({
  user: appState.user
});

const mapDispatchToProps = (dispatch) => ({
  logoutUser: () => dispatch(logout())
});

export default withRouter(withTranslation('appNav')(connect(mapStateToProps, mapDispatchToProps)(AppNav)));



// WEBPACK FOOTER //
// ./src/components/app-nav.js